import type { Citation } from '../api/chatClient';

// Structured signal parsing shared by every AI chat surface (ChatWidget + TeamsSupportPage) so the
// agent's out-of-band markers render identically everywhere:
//   - a model-written "Sources" block  -> collapsible citations (never inline)
//   - [SUGGESTIONS: a | b | c]          -> clickable choice pills
//   - a trailing "Options: a; b; c."    -> the same clickable choice pills
//   - [RESOLVED]                        -> invisible resolution marker (stripped from display)
// Keeping these here means both UXs speak ONE signal contract; the agent instructions are the single
// source of behavior and this is the single source of rendering.

/** Pull a trailing model-written "Sources" block (markdown links / bare URLs) out of the body. */
export function extractSourcesBlock(text: string): { text: string; sources: Citation[] } {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const isHeading = (line: string) =>
    /^[ \t]*(?:\*\*|__)?[ \t]*Sources[ \t]*:?[ \t]*(?:\*\*|__)?[ \t]*$/i.test(line);

  // Use the last "Sources" heading in case the model emits more than one.
  let headingIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (isHeading(lines[i])) headingIdx = i;
  }
  if (headingIdx === -1) return { text, sources: [] };

  const prefix = /^(?:[-*]\s*|\[?\d+\]?[.)]?\s*)?/;
  const mdLink = new RegExp(prefix.source + /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)\s*[.,;:!?]*\s*$/.source, 'i');
  const bareUrl = new RegExp(prefix.source + /(https?:\/\/\S+)\s*$/.source, 'i');

  const sources: Citation[] = [];
  let endIdx = headingIdx + 1; // exclusive index where the block ends
  for (let i = headingIdx + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line === '') {
      endIdx = i + 1; // absorb blank lines inside/after the block
      continue;
    }
    const md = line.match(mdLink);
    if (md) {
      sources.push({ title: md[1].trim(), url: md[2].trim() });
      endIdx = i + 1;
      continue;
    }
    const bare = line.match(bareUrl);
    if (bare) {
      const url = bare[1].trim().replace(/[.,;:!?)\]]+$/, '');
      sources.push({ title: url, url });
      endIdx = i + 1;
      continue;
    }
    break; // first non-source, non-blank line ends the block
  }

  if (sources.length === 0) return { text, sources: [] };

  const cleaned = [...lines.slice(0, headingIdx), ...lines.slice(endIdx)]
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { text: cleaned, sources };
}

// Display-time strip so a model-written "Sources" block never flashes inline while
// streaming (the full block is only moved into the collapsible CitationList in onDone).
export function stripSourcesForDisplay(text: string): string {
  const { text: stripped, sources } = extractSourcesBlock(text);
  if (sources.length > 0) return stripped;
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\n[ \t]*(?:\*\*|__)?[ \t]*Sources[ \t]*:?[ \t]*(?:\*\*|__)?[ \t]*$/i, '')
    .trimEnd();
}

/** True for URLs the citation rules exclude: non-absolute-https, media/asset extensions, or asset hosts/paths. */
function isNonCitableUrl(url: string): boolean {
  if (!/^https:\/\//i.test(url)) return true;
  if (/\.(png|jpe?g|gif|svg|webp|bmp|ico|mp4|mov|webm|pdf|zip)(?:[?#]|$)/i.test(url)) return true;
  if (/akamaihd\.net|azureedge\.net|support\.content\.office\.net/i.test(url)) return true;
  if (/\/(media|images|img|assets|thumb|icon)\//i.test(url)) return true;
  return false;
}

/**
 * Fallback citations: pull inline markdown article links `[label](https://...)` out of the body when
 * no explicit "Sources" block or server-side citations exist, so the collapsible "Sources" affordance
 * stays CONSISTENT on every article-grounded reply (not just the ones where the model happened to append
 * a Sources block). Applies the same asset/relative-URL filtering the agent's §9 citation rules use, and
 * dedupes by normalized URL.
 */
export function extractInlineCitations(text: string): Citation[] {
  const normalized = text.replace(/\r\n/g, '\n');
  const linkRe = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const out: Citation[] = [];
  const seen = new Set<string>();
  for (const m of normalized.matchAll(linkRe)) {
    const title = m[1].trim();
    const url = m[2].trim();
    if (isNonCitableUrl(url)) continue;
    const key = url.toLowerCase().replace(/[#?].*$/, '').replace(/\/$/, '');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ title, url });
  }
  return out;
}

/**
 * Single source of citation-resolution logic shared by every AI chat surface (ChatWidget + Teams) so
 * the "Sources" affordance behaves IDENTICALLY everywhere. Precedence:
 *   1. server-side grounding citations (authoritative), else
 *   2. a model-written "Sources" block parsed out of the body, else
 *   3. inline article links in the body (consistency fallback so a grounded answer always shows Sources).
 * Also strips the model's "Sources" block (markdown and legacy numbered "[1] …") from the returned text
 * so sources render only behind the collapsible link, never inline.
 */
export function resolveCitations(
  serverCitations: Citation[] | undefined,
  text: string,
): { text: string; citations: Citation[] } {
  let finalText = text;
  let citations: Citation[] = serverCitations && serverCitations.length > 0 ? serverCitations : [];

  const block = extractSourcesBlock(finalText);
  if (block.sources.length > 0) {
    finalText = block.text;
    if (citations.length === 0) citations = block.sources;
  }

  // Legacy numbered "Sources \n [1] …" format cleanup when citations are attached.
  if (citations.length > 0) {
    finalText = finalText
      .replace(/\r\n/g, '\n')
      .replace(/\n*\s*Sources\s*\n(\s*\[\d+\][^\n]*\n*)+\s*$/i, '')
      .trimEnd();
  }

  // Consistency fallback: derive citations from inline article links when nothing else attached any.
  if (citations.length === 0) {
    const inline = extractInlineCitations(finalText);
    if (inline.length > 0) citations = inline;
  }

  return { text: finalText, citations };
}

/** Upper bound on rendered choice pills, matching the `[SUGGESTIONS: ...]` contract (max 8 options). */
const MAX_OPTION_PILLS = 8;
/** Options longer than this are prose, not pill labels — used to reject false-positive "Options:" matches. */
const MAX_OPTION_LENGTH = 200;

/** Marker for a trailing plain-text `Options: a; b; c.` list emitted by agents that don't speak the SUGGESTIONS tag. */
const optionsMarkerRegex = /(?:^|[\s])Options[ \t]*:[ \t]*/gi;

/**
 * Parse a trailing `Options: a; b; c.` list (semicolon-separated, or a bullet/numbered list on
 * following lines) into pill labels. Returns `null` when the marker is absent or the tail doesn't
 * look like a real option list, so ordinary prose containing the word "Options:" is left untouched.
 */
function extractOptionsList(normalized: string): { text: string; suggestedPrompts: string[] } | null {
  const markers = Array.from(normalized.matchAll(optionsMarkerRegex));
  const lastMarker = markers.at(-1);
  if (lastMarker?.index === undefined) return null;

  const tail = normalized.slice(lastMarker.index + lastMarker[0].length);
  const rawItems = tail.includes(';')
    ? tail.split(';')
    : tail.split('\n').map(line => line.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, ''));

  const suggestedPrompts = rawItems
    .map(item => item.replace(/\s+/g, ' ').trim())
    // Options are written as sentences ("… on my Windows 11 device."); drop the trailing
    // punctuation so labels read like the `[SUGGESTIONS: ...]` pills.
    .map(item => item.replace(/[.\s]+$/, '').trim())
    .filter(Boolean);

  // Need at least two real, pill-sized options before treating this as a signal.
  if (suggestedPrompts.length < 2) return null;
  if (suggestedPrompts.some(prompt => prompt.length > MAX_OPTION_LENGTH)) return null;

  return {
    text: normalized.slice(0, lastMarker.index).trimEnd(),
    suggestedPrompts: suggestedPrompts.slice(0, MAX_OPTION_PILLS),
  };
}

/**
 * Parse a `[SUGGESTIONS: a | b | c]` block, a trailing `Options: a; b; c.` list, or a trailing
 * "Suggested prompts" list into choice pills.
 */
export function extractSuggestedPrompts(text: string): { text: string; suggestedPrompts: string[] } {
  const normalized = text.replace(/\r\n/g, '\n');
  const suggestionBlockRegex = /\[SUGGESTIONS:\s*([^\]]+)\]/gi;
  const suggestionBlocks = Array.from(normalized.matchAll(suggestionBlockRegex));
  const lastSuggestionBlock = suggestionBlocks.at(-1);
  if (lastSuggestionBlock?.[1]) {
    const suggestedPrompts = lastSuggestionBlock[1]
      .split('|')
      .map(prompt => prompt.trim())
      .filter(Boolean);

    if (suggestedPrompts.length > 0) {
      return {
        text: normalized
          .replace(suggestionBlockRegex, '')
          .replace(/[ \t]+\n/g, '\n')
          .replace(/\n[ \t]+/g, '\n')
          .replace(/\n{3,}/g, '\n\n')
          .trimEnd(),
        suggestedPrompts,
      };
    }
  }

  const options = extractOptionsList(normalized);
  if (options) return options;

  const match = normalized.match(/\n{0,2}(?:#{1,6}\s*)?Suggested prompts\s*:?\s*\n([\s\S]*?)$/i);
  if (!match?.[1]) {
    return { text, suggestedPrompts: [] };
  }

  const suggestedPrompts = match[1]
    .split('\n')
    .map(line => line.match(/^\s*(?:[-*]|\d+[.)])\s+(.+?)\s*$/)?.[1] ?? '')
    .map(prompt => prompt.replace(/^["']|["']$/g, '').trim())
    .filter(Boolean)
    .slice(0, 3);

  if (suggestedPrompts.length === 0) {
    return { text, suggestedPrompts: [] };
  }

  return {
    text: normalized.slice(0, match.index).trimEnd(),
    suggestedPrompts,
  };
}

/** Remove the invisible [RESOLVED] resolution marker from any position in the text. */
export function stripResolvedTag(text: string): { text: string; resolved: boolean } {
  const resolved = /\[RESOLVED\]/i.test(text);
  if (!resolved) return { text, resolved: false };
  const stripped = text
    .replace(/\[RESOLVED\]/gi, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { text: stripped, resolved: true };
}

/**
 * Display-time strip of every out-of-band signal (Sources block, [SUGGESTIONS:...], `Options:` list,
 * [RESOLVED]) so raw markers never flash while tokens stream. The structured values are recovered
 * separately in onDone.
 */
export function stripSignalsForDisplay(text: string): string {
  const withoutSources = stripSourcesForDisplay(text);
  const withoutSuggestions = extractSuggestedPrompts(withoutSources).text
    // A still-streaming `Options:` list (only the first item has arrived) isn't parsed as pills
    // yet; hide it so it doesn't flash. Only a semicolon-separated tail counts as a list.
    .replace(/(?:^|[\s])Options[ \t]*:[ \t]*[^\n]*;[\s\S]*$/i, '')
    .trimEnd();
  return stripResolvedTag(withoutSuggestions).text;
}
